//
// # WorkspacePlatform
//
// A `Platform` implementation for the IDE that routes data requests directly
// to AlphaClient. For in-memory hosts, implementing Platform directly avoids
// the message serialization overhead used by VsCode and Web environments.
//
// Each Editor instance gets its own WorkspacePlatform bound to a specific Ident.
//

import { logger, msgs, Result } from "CoreUtils";
import { Platform, PlatformError, SelectionInfo } from "EditorCore";
import { model, Ident, layout } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import type { Editor } from "EditorCore";
import { createRequestContext } from "./contextHelpers";
import type { Workspace } from "./workspace";
import {
  GenerateFromSourceError,
  type GenerationReport,
} from "ServerInterface";
import type { PinDirection } from "./types";
import { oaCellviewIdent } from "./components/DesignExplorer/pathInput";

// Feature flag for batch hierarchy loading.
// Set to true to use open-hierarchy (single request for all layouts).
// Set to false to use sequential open requests (old behavior).
export const USE_BATCH_HIERARCHY_LOAD = true;

export class WorkspacePlatform implements Platform {
  readonly name = "WorkspacePlatform";

  // Editor reference, set by the Editor after construction via setEditor().
  private editorRef: Editor | null = null;

  // Pre-loaded content cache for batch-loaded hierarchies.
  // Keyed by JSON-stringified ident for reliable lookup.
  // Populated by Workspace.handleOpenRequest using openHierarchy.
  readonly contentCache = new Map<string, model.ContentJson>();

  // Marks the cache as the authoritative result of an openHierarchy walk:
  // the server pre-walked the full transitive closure of dependent content,
  // so any miss here is a bug (e.g., a stale reference, missing pre-walk
  // case, or corrupt depot entry). When true, getContent fails loudly on
  // miss instead of silently falling through to a fresh HTTP fetch.
  private cacheIsComplete = false;

  constructor(private readonly workspace: Workspace, readonly ident: Ident) {}

  // Pre-populate the content cache with content pre-fetched via openHierarchy.
  // Marks the cache as complete; subsequent getContent misses are treated as
  // hard errors.
  populateCache(contents: Array<{ ident: unknown; content: unknown }>): {
    cached: number;
    failed: number;
  } {
    let cached = 0;
    let failed = 0;
    for (const { ident, content } of contents) {
      // Validate ident and use Ident.stringify for consistent cache keys
      const identResult = Ident.validator.validate(ident);
      if (identResult.err) {
        failed++;
        continue;
      }
      const key = Ident.stringify(identResult.val);
      const valResult = model.ContentJson.validator.validate(content);
      if (valResult.ok) {
        this.contentCache.set(key, valResult.val);
        cached++;
      } else {
        failed++;
      }
    }
    this.cacheIsComplete = true;
    return { cached, failed };
  }

  // Clear the content cache after loading is complete.
  clearCache(): void {
    this.contentCache.clear();
    this.cacheIsComplete = false;
  }

  // Evict a single cached entry so its next getContent re-fetches from the
  // server. Used on a dependent-design refresh to drop just the changed
  // child's content (the rest of the pre-walked hierarchy stays cached). Drops
  // the "complete" claim so the resulting miss falls through to a fetch rather
  // than failing loud.
  evictContent(ident: Ident): void {
    this.contentCache.delete(Ident.stringify(ident));
    this.cacheIsComplete = false;
  }

  // Drop the "complete" claim, keeping the cached entries. A change can add a
  // target the openHierarchy pre-walk never saw, so a miss is legitimate.
  markCacheIncomplete(): void {
    this.cacheIsComplete = false;
  }

  // Called by the Editor after construction.
  setEditor(editor: Editor): void {
    this.editorRef = editor;
  }

  //
  // ## Data Access Methods
  //
  // Call AlphaClient directly — no message serialization needed.
  //

  async getPdk(pdkId: string): Promise<Result<schema.pdk.PDK, PlatformError>> {
    const ctx = createRequestContext(this.workspace.userId);
    const result = await this.workspace.client.request(ctx, {
      kind: "get-pdk",
      data: pdkId,
    });
    if (result.err) {
      return Result.Err({ why: "request-failed", data: result.val });
    }
    const valResult = schema.validators.PDK.validator.validate(result.val);
    if (valResult.err) {
      return Result.Err({ why: "invalid-pdk-response", data: valResult.val });
    }
    return Result.Ok(valResult.val);
  }

  async getStack(
    pdkId: string,
    stackId: schema.StackId
  ): Promise<Result<schema.pdk.Stack, PlatformError>> {
    const ctx = createRequestContext(this.workspace.userId);
    const result = await this.workspace.client.request(ctx, {
      kind: "get-stack",
      data: { pdkId, stackId },
    });
    if (result.err) {
      return Result.Err({ why: "request-failed", data: result.val });
    }
    const valResult = model.layout.Stack.validator.validate(result.val);
    if (valResult.err) {
      return Result.Err({ why: "invalid-stack-response", data: valResult.val });
    }
    return Result.Ok(valResult.val);
  }

  async getContent(
    ident: model.Ident
  ): Promise<Result<model.ContentJson, PlatformError>> {
    // Check the pre-loaded cache first (populated by openHierarchy)
    // Use Ident.stringify for consistent cache keys (property order independent)
    const cacheKey = Ident.stringify(ident);
    const cached = this.contentCache.get(cacheKey);
    if (cached) {
      return Result.Ok(cached);
    }

    // Cache is the authoritative result of an openHierarchy walk: the server
    // already pre-walked everything we should need. A miss here means we
    // either asked for content that isn't actually referenced from the root,
    // or the server's pre-walk missed something it should have included. Fail
    // loudly so the bug surfaces instead of papering over it with another
    // round-trip.
    if (this.cacheIsComplete) {
      return Result.Err({
        why: "content-not-in-hierarchy-cache",
        data: `getContent miss after openHierarchy pre-walk: ${Ident.displayName(
          ident
        )}`,
      });
    }

    const ctx = createRequestContext(this.workspace.userId);
    // 60s timeout: child layouts loaded during Layout.fromSchema() may require
    // GCS fetches with cold-start latency. Default 10s is insufficient.
    const result = await this.workspace.client.request(
      ctx,
      { kind: "open", data: { kind: "ident", ident } },
      60_000
    );
    if (result.err) {
      return Result.Err({ why: "request-failed", data: result.val });
    }
    const valResult = model.ContentJson.validator.validate(result.val);
    if (valResult.err) {
      return Result.Err({
        why: "invalid-content-response",
        data: valResult.val,
      });
    }
    return Result.Ok(valResult.val);
  }

  //
  // ## Notification Methods
  //
  // One-way notifications from the Editor to the IDE workspace.
  //

  notifyEditorUp(): void {
    logger.log(
      `[WorkspacePlatform] Editor up: ${Ident.displayName(this.ident)}`
    );
  }

  notifyUIChange(change: model.Change): void {
    const ctx = createRequestContext(this.workspace.userId);
    // Track the promise so workspace.save() can flush in-flight "changes"
    // requests before dispatching "save", preventing the race where a
    // manual save overtakes a preceding edit on the server.
    const promise = this.workspace.client.request(ctx, {
      kind: "changes",
      data: {
        changes: [change],
        source: "gui",
        ident: this.ident,
      },
    });
    this.workspace.trackPendingChange(promise);
    this.workspace.markDirty(this.ident);
    // Local edits (canvas move/rotate, panel Apply) don't go through a
    // design-load refresh, so refresh the Properties panel's view of the
    // selected entity here.
    this.workspace.properties.notifyLocalEdit();
    // Any committed edit can move, delete, or fix the entities the last Check &
    // Save run flagged, so its results stop describing the schematic. This is
    // the one funnel every edit reaches — direct canvas edits, panel Applies,
    // and undo/redo (which call sendChangeMessage without touching the change
    // log) — so it is the right place to invalidate. No-op when there are no
    // results to invalidate.
    this.workspace.srv.markResultsStale();
  }

  notifySelectionChanged(sel: SelectionInfo | null): void {
    // Route the selection change to the workspace, keyed by this editor's
    // ident, so the Properties Panel can derive and render its view model.
    this.workspace.properties.setSelection(this.ident, sel);
  }

  notifyEditInstanceParameters(instanceId: string): void {
    // Open the "Edit Parameters" dialog for this instance.
    this.workspace.openEditInstanceDialog(this.ident, instanceId);
  }

  notifyAddDummyDevices(instanceIds: number[], instanceNames: string[]): void {
    this.workspace.openDummyDeviceDialog(
      this.ident,
      instanceIds,
      instanceNames
    );
  }

  notifyAddInstanceAtPosition(position: { x: number; y: number }): void {
    // Open the "Add Instance" dialog at the given canvas position.
    this.workspace.openAddInstanceDialog(this.ident, position);
  }

  notifyAddPinAtPosition(position: { x: number; y: number }): void {
    // Open the pin dialog in "create" mode at the given canvas position.
    this.workspace.openCreatePinDialog(this.ident, position);
  }

  notifyRenameInstance(instanceId: string): void {
    // Open the "Rename Instance" dialog for this instance.
    this.workspace.openRenameInstanceDialog(this.ident, instanceId);
  }

  notifyRenamePin(pinId: number, currentName: string): void {
    // Open the "Rename Pin" dialog for this pin.
    this.workspace.openRenamePinDialog(this.ident, pinId, currentName);
  }

  notifyEditPinProperties(pinId: number, currentDir: PinDirection): void {
    // Open the pin dialog in "editProperties" mode for this pin.
    this.workspace.openEditPinPropertiesDialog(this.ident, pinId, currentDir);
  }

  notifyEditWireLabel(wireId: number, currentLabel: string): void {
    // Open the "Edit Wire Label" dialog for this wire.
    this.workspace.openEditWireLabelDialog(this.ident, wireId, currentLabel);
  }

  notifyEditRouteWidth(params: layout.EditRouteWidthParams): void {
    // Open the "Set Route Width" dialog for this design.
    this.workspace.openEditRouteWidthDialog(this.ident, params);
  }

  notifyAddTextAnnotation(position: { x: number; y: number }): void {
    // Open the "Add Text" annotation dialog at this position.
    this.workspace.openAddTextAnnotationDialog(this.ident, position);
  }

  notifyEditTextAnnotation(elementId: number, currentText: string): void {
    // Open the "Edit Text" annotation dialog for this element.
    this.workspace.openEditTextAnnotationDialog(
      this.ident,
      elementId,
      currentText
    );
  }

  notifyGenerateFromSourceRequest(): void {
    void this.doGenerateFromSource();
  }

  notifyOpenInstanceTarget(target: model.Ident): void {
    this.workspace.openWithBreadcrumb(target, this.ident);
  }

  notifyViewInDepot(target: model.Ident): void {
    // Reveal the target design in the Depot tree.
    this.workspace.revealInDepot(target);
  }

  notifyViewCurrentDesignInDepot(): void {
    // Reveal this design in the Depot tree.
    this.workspace.revealInDepot(this.ident);
  }

  notifyOpenLinkedSchematic(): void {
    this.workspace.openLinkedSchematic(this.ident);
  }

  notifyOpenLinkedSymbol(): void {
    this.workspace.openLinkedSymbol(this.ident);
  }

  notifyOpenSchematicForInstance(
    schematicImpls: ReadonlyArray<schema.symbol.SchematicImplRef>,
    pdkId?: string
  ): void {
    this.workspace.navigateToSchematicImpls(schematicImpls, pdkId);
  }

  notifyAddSymbolPin(position: { x: number; y: number }): void {
    this.workspace.openAddSymbolPinDialog(this.ident, position);
  }

  notifyEditSymbolPin(pinData: {
    portName: string;
    width: number;
    dir: schema.core.PortDir;
    labelPosition: string;
    id?: number;
  }): void {
    this.workspace.openEditSymbolPinDialog(this.ident, pinData);
  }

  notifyAddLayoutPin(position: { x: number; y: number }): void {
    this.workspace.openAddLayoutPinDialog(this.ident, position);
  }

  notifyEditLayoutPin(pinData: {
    name: string;
    layer: number;
    pinType: schema.core.PinType | undefined;
    id: number;
  }): void {
    this.workspace.openEditLayoutPinDialog(this.ident, pinData);
  }

  notifyAddExprLabel(position: { x: number; y: number }): void {
    this.workspace.openAddExprLabelDialog(this.ident, position);
  }

  notifyEditExprLabel(labelData: {
    kind: schema.symbol.ExprKind;
    name?: string;
    paramName?: string;
    id: number;
  }): void {
    this.workspace.openEditExprLabelDialog(this.ident, labelData);
  }

  notifyOpenGraphicsCatalogue(): void {
    this.workspace.openGraphicsCatalogueDialog();
  }

  notifyConfigurePowerRouting(): void {
    this.workspace.openPowerRoutingDialog(this.ident);
  }

  notifyGenerateFromSource(): void {
    void this.doGenerateFromSource();
  }

  notifyShowInHierarchy(instanceName: string, instanceId: number): void {
    this.workspace.showInHierarchy(instanceName, instanceId);
  }

  notifySaveAsRequested(): void {
    this.workspace.openSaveAsDialog();
  }

  hasParentBreadcrumb(): boolean {
    return this.workspace.hasParentBreadcrumb(this.ident);
  }

  notifyOpenParent(): void {
    this.workspace.openParent(this.ident);
  }

  private async doGenerateFromSource(): Promise<void> {
    const ctx = createRequestContext(this.workspace.userId);

    // Load content for this.ident, validate it's a schematic
    const contentResult = await this.workspace.client.request(ctx, {
      kind: "open",
      data: { kind: "ident", ident: this.ident },
    });
    if (contentResult.err) {
      logger.error(
        `[WorkspacePlatform] Failed to load content: ${JSON.stringify(
          contentResult.val
        )}`
      );
      this.editorRef?.showFeedback(
        "Could not open the source schematic to generate a layout.",
        6000
      );
      return;
    }
    const content = contentResult.val as {
      contentKind?: string;
      pdkId?: string;
    };
    if (content.contentKind !== "schematic") {
      logger.warn("[WorkspacePlatform] Not a schematic, cannot create layout");
      this.editorRef?.showFeedback(
        "Generate Layout requires a schematic as the source.",
        6000
      );
      return;
    }

    // Extract pdkId. A layout is always PDK-specific, so a schematic with no
    // PDK set can't seed one — tell the user how to fix it rather than failing
    // silently.
    const pdkId = content.pdkId;
    if (!pdkId) {
      logger.error("[WorkspacePlatform] Schematic has no pdkId");
      this.editorRef?.showFeedback(
        "This schematic has no PDK set. A layout needs a PDK — set the schematic's PDK, then generate the layout again.",
        8000
      );
      return;
    }

    // List stacks
    const stacksResult = await this.workspace.listStacks(pdkId);
    if (stacksResult.err) {
      logger.error(
        `[WorkspacePlatform] Failed to list stacks: ${JSON.stringify(
          stacksResult.val
        )}`
      );
      this.editorRef?.showFeedback(
        `Could not load metal stacks for PDK "${pdkId}".`,
        6000
      );
      return;
    }
    const stacks = stacksResult.val;
    if (stacks.length === 0) {
      logger.error("[WorkspacePlatform] No stacks available");
      this.editorRef?.showFeedback(
        `PDK "${pdkId}" has no metal stacks available to generate a layout.`,
        6000
      );
      return;
    }

    // Derive a default layout path from the schematic ident. A path holds one
    // design (its type is its contentKind), so the layout can't reuse the
    // schematic's path — pick a non-colliding sibling leaf. When the schematic
    // uses the default leaf "schematic", swap it for "layout" (echoing the old
    // library/cell/view default); otherwise suffix "_layout". The user can edit
    // it before creating.
    let defaultPath = "";
    // Pre-filled OpenAccess address. A depot schematic leaves `lib` blank: a
    // guess would create a library on the first save.
    let defaultOa = { lib: "", cell: "", view: "layout" };
    if (this.ident.kind === "hierpath") {
      const parts = [...this.ident.path];
      if (parts.length > 0) {
        const leaf = parts[parts.length - 1];
        parts[parts.length - 1] =
          leaf === "schematic" ? "layout" : `${leaf}_layout`;
      } else {
        parts.push("layout");
      }
      defaultPath = parts.join("/");
      // The cell is the parent segment (library/cell/schematic), else the leaf.
      const path = this.ident.path;
      let cell = "";
      if (path.length > 1) {
        cell = path[path.length - 2];
      } else if (path.length === 1) {
        cell = path[0];
      }
      defaultOa = { lib: "", cell, view: "layout" };
    } else if (this.ident.kind === "libcellview") {
      // OA schematic - keep the familiar lib/cell/layout form, matching
      // the hierpath default above.
      defaultPath = `${this.ident.lib}/${this.ident.cell}/layout`;
      defaultOa = {
        lib: this.ident.lib,
        cell: this.ident.cell,
        view: "layout",
      };
    }

    // Show combined stack + destination dialog
    const result = await this.workspace.promptCreateLayout(
      stacks,
      defaultPath,
      defaultOa
    );
    if (!result) return; // cancelled

    // The destination only sets the layout's ident; the first save writes it.
    const dest = result.destination;
    const layoutIdent: Ident =
      dest.kind === "openaccess"
        ? oaCellviewIdent(dest.lib, dest.cell, dest.view)
        : {
            kind: "hierpath",
            path: dest.layoutPath.split("/").filter((s) => s.length > 0),
          };

    // Warn if a design already exists at this path. Depot only: for OA the
    // gateway refuses a stale overwrite at save time.
    // An existing cellview is asked about then: see `Workspace.requestSave`.
    if (dest.kind === "depot") {
      const exists = await this.workspace.contentExistsDepot(layoutIdent);
      if (exists) {
        const overwrite = await this.workspace.promptOverwrite(dest.layoutPath);
        if (!overwrite) return;
      }
    }

    // Call generate-from-source. 45s timeout: the schematic can contain
    // external-generator (Custom Compiler) cells, which run synchronously on
    // the daemon and can take tens of seconds — the default 10s would abort
    // a valid in-flight generation.
    //
    // Now 4 minutes: AlphaServer makes one Custom Compiler call at a time
    // (one license), ~16s each, so a first conversion of a schematic with 5
    // distinct cells needs ~80s and timed out at 45s, though the server went
    // on and finished. Kept under Node's default 300s `requestTimeout`, so the
    // server does not drop the request first.
    const layoutResult = await this.workspace.client.request(
      ctx,
      {
        kind: "generate-from-source",
        data: {
          schematicIdent: this.ident,
          pdkId,
          stackId: result.stackId,
          layoutIdent,
          rectangular: result.rectangular,
        },
      },
      240_000
    );
    if (layoutResult.err) {
      // A structured generation report (preflight block or incomplete
      // conversion) gets printed to the Console panel, instance by instance,
      // and the panel is focused so the user knows where to look. Anything
      // else is an unexpected failure — a short toast plus the console.
      const validated = GenerateFromSourceError.validator.validate(
        layoutResult.val
      );
      if (validated.ok) {
        this.renderGenerationReport(validated.val.data);
      } else {
        logger.error(
          `[WorkspacePlatform] Failed to create layout: ${JSON.stringify(
            layoutResult.val
          )}`
        );
        this.workspace.logToConsole(
          "error",
          "generate-layout",
          `Layout generation failed: ${JSON.stringify(layoutResult.val)}`
        );
        this.editorRef?.showFeedback(
          "Layout generation failed — see the Console.",
          6000
        );
      }
      this.workspace.showPanel("bottom");
      return;
    }

    // Open the resulting layout and mark dirty so the user can save it
    const resultIdent = layoutResult.val as Ident;
    await this.workspace.handleOpenRequest(resultIdent);
    this.workspace.markDirty(resultIdent);
  }

  // Print a layout-generation report to the in-app Console panel, one line per
  // blocking instance, and pop a short toast summarising the count. The caller
  // focuses the Console panel. Used for both the pre-conversion block and the
  // (rare) post-conversion shortfall.
  private renderGenerationReport(report: GenerationReport): void {
    const n = report.issues.length;
    this.workspace.logToConsole(
      "error",
      "generate-layout",
      `Cannot generate layout: ${n} instance${
        n === 1 ? "" : "s"
      } cannot be placed (${report.instancesConverted}/${
        report.instancesTotal
      } placeable). Fix the following, then generate again:`
    );
    for (const issue of report.issues) {
      const detail =
        issue.details && issue.details.length > 0
          ? ` (${issue.details.join("; ")})`
          : "";
      this.workspace.logToConsole(
        "error",
        "generate-layout",
        `  • ${issue.message}${detail}`
      );
    }
    this.editorRef?.showFeedback(
      `Layout generation blocked: ${n} unplaceable instance${
        n === 1 ? "" : "s"
      } — see the Console.`,
      8000
    );
  }

  sendMainContent(_content: schema.Content): void {}

  sendMainContentResponse(_uuid: string, _content: schema.Content): void {}

  sendUpdateMainContentResponse(
    _uuid: string,
    _result: Result<void, unknown>
  ): void {}

  sendRefreshMainContentResponse(
    _uuid: string,
    _result: Result<void, unknown>
  ): void {}

  sendCaptureCanvasResponse(
    _uuid: string,
    _result: Result<string, string>
  ): void {}

  log(body: unknown): void {
    logger.log(body);
  }

  warn(body: unknown): void {
    logger.warn(body);
  }

  error(body: unknown): void {
    logger.error(body);
  }
}
